import React, { useState } from 'react';
import { RefreshCw, ClipboardPaste, Check, AlertTriangle, AlertCircle } from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, doc, writeBatch, serverTimestamp } from 'firebase/firestore';

const SyncSchedule = ({ selectedClub, teams, tournaments, games, onBack }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);

  const handlePreview = () => {
    setError('');
    setPreviewData(null);
    setSyncSuccess(false);

    if (!selectedTeamId || !selectedTournamentId) {
      setError('Please select both a Team and a Tournament.');
      return;
    }

    try {
      const parsedData = JSON.parse(jsonInput);
      if (!Array.isArray(parsedData)) {
        throw new Error('Data must be an array of games.');
      }

      // Find existing games for this team + tournament combo
      const existingGames = games.filter(
        g => g.teamId === selectedTeamId && g.tournamentId === selectedTournamentId
      );

      const team = teams.find(t => t.id === selectedTeamId);
      const teamNameLower = team.name.toLowerCase();
      
      let newGames = [];
      let updatedGames = [];
      let untouchedGames = [];

      parsedData.forEach(pGameRaw => {
        if (!pGameRaw.date || !pGameRaw.opponent) return;
        
        let pGame = { ...pGameRaw };
        
        // Extract actual opponent from "Team A v Team B"
        const lowerOpponent = pGame.opponent.toLowerCase();
        let isHome = true; // default
        
        if (lowerOpponent.includes(' v ') || lowerOpponent.includes(' vs ')) {
           const parts = pGame.opponent.split(/\s+vs?\s+/i).map(p => p.trim());
           if (parts.length === 2) {
             if (parts[1].toLowerCase().includes(teamNameLower)) {
                pGame.opponent = parts[0];
                isHome = false;
             } else if (parts[0].toLowerCase().includes(teamNameLower)) {
                pGame.opponent = parts[1];
                isHome = true;
             } else {
                // If we can't match either side strictly, keep the full string or pick one randomly.
                // It's safer to keep it as is if we can't determine it.
             }
           }
        }
        
        pGame.isHome = isHome;

        // Try to find a matching game by Date and Opponent
        // (Date format might differ, so we do a fuzzy match or exact match on the string)
        const match = existingGames.find(eg => {
          // Compare opponent (fuzzy)
          const isOpponent = eg.opponent?.toLowerCase() === pGame.opponent.toLowerCase() || 
                             eg.homeTeam?.toLowerCase().includes(pGame.opponent.toLowerCase()) ||
                             eg.awayTeam?.toLowerCase().includes(pGame.opponent.toLowerCase());
          
          // Compare date roughly
          const egDate = new Date(eg.date).toLocaleDateString();
          const pDate = new Date(pGame.date).toLocaleDateString();
          
          return isOpponent && egDate === pDate;
        });

        if (match) {
          // Check if time or venue changed
          if (match.time !== pGame.time || match.address !== pGame.venue) {
            updatedGames.push({
              id: match.id,
              ...pGame,
              changes: []
            });
            if (match.time !== pGame.time) updatedGames[updatedGames.length-1].changes.push(`Time: ${match.time} -> ${pGame.time}`);
            if (match.address !== pGame.venue) updatedGames[updatedGames.length-1].changes.push(`Venue: ${match.address} -> ${pGame.venue}`);
          } else {
            untouchedGames.push(pGame);
          }
        } else {
          newGames.push(pGame);
        }
      });

      setPreviewData({ newGames, updatedGames, untouchedGames });

    } catch (err) {
      setError('Invalid JSON payload. Did you copy it correctly from the Bookmarklet? Error: ' + err.message);
    }
  };

  const handleSync = async () => {
    if (!previewData) return;
    setIsSyncing(true);
    setError('');

    try {
      const batch = writeBatch(db);
      const team = teams.find(t => t.id === selectedTeamId);
      const tournament = tournaments.find(t => t.id === selectedTournamentId);

      // Add new games
      for (const ng of previewData.newGames) {
        const gameRef = doc(collection(db, 'games'));
        
        // Parse date correctly
        let isoDate = '';
        try {
           isoDate = new Date(`${ng.date} ${ng.time}`).toISOString();
        } catch(e) {
           isoDate = new Date(ng.date).toISOString();
        }

        batch.set(gameRef, {
          clubId: selectedClub.id,
          teamId: selectedTeamId,
          teamName: team.name,
          tournamentId: selectedTournamentId,
          tournamentName: tournament.name,
          opponent: ng.opponent,
          homeTeam: ng.isHome ? team.name : ng.opponent,
          awayTeam: ng.isHome ? ng.opponent : team.name,
          date: isoDate,
          time: ng.time,
          address: ng.venue,
          status: 'upcoming',
          createdAt: serverTimestamp()
        });
      }

      // Update existing games
      for (const ug of previewData.updatedGames) {
        const gameRef = doc(db, 'games', ug.id);
        
        let isoDate = '';
        try {
           isoDate = new Date(`${ug.date} ${ug.time}`).toISOString();
        } catch(e) {
           isoDate = new Date(ug.date).toISOString();
        }

        batch.update(gameRef, {
          time: ug.time,
          address: ug.venue,
          date: isoDate,
          updatedAt: serverTimestamp()
        });
      }

      await batch.commit();
      setSyncSuccess(true);
      setPreviewData(null);
      setJsonInput('');
    } catch (err) {
      console.error(err);
      setError('Failed to sync to database: ' + err.message);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-6 sm:p-8">
      <button onClick={onBack} className="text-gray-600 mb-6 hover:text-gray-800 flex items-center gap-2">
        ← Back to Overview
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-teal-100 text-teal-600 rounded-lg">
          <RefreshCw size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-bold">Sync CricClubs Schedule</h2>
          <p className="text-gray-600">Import your team's schedule directly from NWCL/CricClubs.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold mb-3">1. Select Target</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Team *</label>
              <select 
                value={selectedTeamId} 
                onChange={(e) => setSelectedTeamId(e.target.value)}
                className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">-- Choose a Team --</option>
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Tournament *</label>
              <select 
                value={selectedTournamentId} 
                onChange={(e) => setSelectedTournamentId(e.target.value)}
                className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">-- Choose a Tournament --</option>
                {tournaments.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3">2. How to get data</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <li>Create a new Bookmark in your browser.</li>
            <li>Set the URL to the code shown below.</li>
            <li>Go to the NWCL schedule page.</li>
            <li>Click your bookmarklet. It will copy the schedule.</li>
            <li>Paste it in the box below!</li>
          </ol>
          <div className="mt-3">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Bookmarklet Code (Copy this)</label>
            <div className="flex">
              <input 
                type="text" 
                readOnly 
                value="javascript:(function() { function parseSchedule() { let games = []; let allLines = document.body.innerText.split('\\n').map(l => l.trim()).filter(l => l.length > 0); let teamLineIndices = []; for (let i = 0; i < allLines.length; i++) { if (allLines[i].match(new RegExp('\\\\b(v|vs|versus)\\\\b', 'i'))) { teamLineIndices.push(i); } } for (let j = 0; j < teamLineIndices.length; j++) { let i = teamLineIndices[j]; let isStandalone = allLines[i].match(new RegExp('^\\\\s*(v|vs|versus)\\\\s*$', 'i')); let teamsLine = allLines[i]; if (isStandalone && i > 0 && i < allLines.length - 1) { teamsLine = allLines[i-1] + &quot; &quot; + allLines[i] + &quot; &quot; + allLines[i+1]; } let startIdx = j === 0 ? Math.max(0, i - 15) : Math.max(teamLineIndices[j-1] + 1, i - 15); let endIdx = j === teamLineIndices.length - 1 ? Math.min(allLines.length, i + 15) : Math.min(teamLineIndices[j+1], i + 15); let windowLines = allLines.slice(startIdx, endIdx); let timeLine = windowLines.find(l => l.match(new RegExp('\\\\d{1,2}:\\\\d{2}\\\\s*(AM|PM|am|pm)', 'i'))); let timeText = &quot;09:00 AM&quot;; if (timeLine) { let tMatch = timeLine.match(new RegExp('\\\\d{1,2}:\\\\d{2}\\\\s*(AM|PM|am|pm)', 'i')); if (tMatch) timeText = tMatch[0]; } let dateLine = windowLines.find(l => l.match(new RegExp('(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\\\\s+\\\\d{4}', 'i')) || l.match(new RegExp('\\\\d{1,2}/\\\\d{1,2}/\\\\d{4}'))); let dayNumIndex = windowLines.findIndex(l => l.match(new RegExp('^\\\\d{1,2}$'))); let dayNum = dayNumIndex !== -1 ? windowLines[dayNumIndex] : null; let dateText = dateLine ? (dayNum && !dateLine.match(new RegExp('^\\\\d{1,2}/')) ? `${dayNum} ${dateLine}` : dateLine) : &quot;TBD&quot;; let venueLine = windowLines.find(l => !l.match(new RegExp('\\\\b(v|vs|versus)\\\\b', 'i')) && (l.includes('@') || l.match(new RegExp('\\\\b(venue|ground|park|field|oval|stadium)\\\\b', 'i'))) && !l.match(new RegExp('(umpire|toss|fielding|batting|status|result)', 'i'))); let venueText = &quot;TBD&quot;; if (venueLine) { if (venueLine.includes('@')) { venueText = venueLine.split('@')[1].trim(); } else if (venueLine.includes(':')) { venueText = venueLine.substring(venueLine.indexOf(':') + 1).trim(); } else { venueText = venueLine; } venueText = venueText.split(new RegExp('(Umpires|Match|Status)', 'i'))[0].trim(); } let opponentText = teamsLine.replace(new RegExp('(📝|🏆|📄)', 'g'), '').trim(); if (opponentText.length > 5) { games.push({ date: dateText, time: timeText, opponent: opponentText, venue: venueText }); } } if (games.length === 0) { let tables = document.querySelectorAll('table'); let headers = {}; let targetTable = null; for (let table of tables) { let firstRow = table.querySelector('tr'); if (!firstRow) continue; let ths = Array.from(firstRow.querySelectorAll('th, td')).map(th => th.innerText.trim().toLowerCase()); if (ths.some(h => h.includes('date') || h.includes('match') || h.includes('opponent') || h.includes('versus') || h === 'vs' || h === 'v')) { targetTable = table; ths.forEach((text, i) => { if (text.includes('date')) { if (headers['date'] === undefined) headers['date'] = i; } else if ((text.includes('match') && !text.includes('type') && !text.includes('status')) || text.includes('teams') || text.includes('opponent') || text.includes('versus') || text === 'vs' || text === 'v') { if (headers['opponent'] === undefined) headers['opponent'] = i; } else if (text.includes('ground') || text.includes('venue') || text.includes('location')) { if (headers['venue'] === undefined) headers['venue'] = i; } else if (text.includes('time')) { if (headers['time'] === undefined) headers['time'] = i; } }); break; } } if (targetTable) { if (headers['date'] === undefined) headers['date'] = 0; if (headers['opponent'] === undefined) headers['opponent'] = 1; if (headers['venue'] === undefined) headers['venue'] = 2; let rows = targetTable.querySelectorAll('tr'); for (let i = 1; i < rows.length; i++) { let cells = rows[i].querySelectorAll('td'); if (cells.length < 3) continue; let dateText = cells[headers['date']]?.innerText.trim(); let opponentText = cells[headers['opponent']]?.innerText.trim(); let venueText = cells[headers['venue']]?.innerText.trim(); let timeText = headers['time'] !== undefined ? cells[headers['time']]?.innerText.trim() : null; if (dateText && !timeText && dateText.match(new RegExp('\\\\d{1,2}:\\\\d{2}'))) { let parts = dateText.split('\\n'); if (parts.length > 1) { dateText = parts[0].trim(); timeText = parts[1].trim(); } else { let timeMatch = dateText.match(new RegExp('(\\\\d{1,2}:\\\\d{2}\\\\s*(AM|PM|am|pm)?)')); if (timeMatch) { timeText = timeMatch[0]; dateText = dateText.replace(timeText, '').trim(); } } } if (dateText && opponentText) { games.push({ date: dateText, time: timeText || &quot;09:00 AM&quot;, opponent: opponentText.replace(new RegExp('\\\\s+', 'g'), ' '), venue: venueText || &quot;TBD&quot; }); } } } } if (games.length === 0) { alert(&quot;Could not find any schedule cards. The table format might be different.&quot;); return; } let uniqueGames = []; let seen = new Set(); for (let g of games) { let key = g.date + g.opponent; if (!seen.has(key)) { seen.add(key); uniqueGames.push(g); } } let json = JSON.stringify(uniqueGames, null, 2); navigator.clipboard.writeText(json).then(function() { alert(&quot;Copied &quot; + uniqueGames.length + &quot; games to clipboard!\\nNow go to Clubhudl Admin Dashboard -> Sync Schedule to paste it.&quot;); }, function(err) { prompt(&quot;Failed to auto-copy. Please manually copy this JSON (Cmd/Ctrl+C):&quot;, json); }); } parseSchedule();})();"
                className="w-full text-xs font-mono bg-gray-100 border border-gray-300 rounded-l-md px-2 py-1"
                onClick={e => e.target.select()}
              />
              <button 
                onClick={() => navigator.clipboard.writeText("javascript:(function() { function parseSchedule() { let games = []; let allLines = document.body.innerText.split('\\n').map(l => l.trim()).filter(l => l.length > 0); let teamLineIndices = []; for (let i = 0; i < allLines.length; i++) { if (allLines[i].match(new RegExp('\\\\b(v|vs|versus)\\\\b', 'i'))) { teamLineIndices.push(i); } } for (let j = 0; j < teamLineIndices.length; j++) { let i = teamLineIndices[j]; let isStandalone = allLines[i].match(new RegExp('^\\\\s*(v|vs|versus)\\\\s*$', 'i')); let teamsLine = allLines[i]; if (isStandalone && i > 0 && i < allLines.length - 1) { teamsLine = allLines[i-1] + \" \" + allLines[i] + \" \" + allLines[i+1]; } let startIdx = j === 0 ? Math.max(0, i - 15) : Math.max(teamLineIndices[j-1] + 1, i - 15); let endIdx = j === teamLineIndices.length - 1 ? Math.min(allLines.length, i + 15) : Math.min(teamLineIndices[j+1], i + 15); let windowLines = allLines.slice(startIdx, endIdx); let timeLine = windowLines.find(l => l.match(new RegExp('\\\\d{1,2}:\\\\d{2}\\\\s*(AM|PM|am|pm)', 'i'))); let timeText = \"09:00 AM\"; if (timeLine) { let tMatch = timeLine.match(new RegExp('\\\\d{1,2}:\\\\d{2}\\\\s*(AM|PM|am|pm)', 'i')); if (tMatch) timeText = tMatch[0]; } let dateLine = windowLines.find(l => l.match(new RegExp('(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\\\\s+\\\\d{4}', 'i')) || l.match(new RegExp('\\\\d{1,2}/\\\\d{1,2}/\\\\d{4}'))); let dayNumIndex = windowLines.findIndex(l => l.match(new RegExp('^\\\\d{1,2}$'))); let dayNum = dayNumIndex !== -1 ? windowLines[dayNumIndex] : null; let dateText = dateLine ? (dayNum && !dateLine.match(new RegExp('^\\\\d{1,2}/')) ? `${dayNum} ${dateLine}` : dateLine) : \"TBD\"; let venueLine = windowLines.find(l => !l.match(new RegExp('\\\\b(v|vs|versus)\\\\b', 'i')) && (l.includes('@') || l.match(new RegExp('\\\\b(venue|ground|park|field|oval|stadium)\\\\b', 'i'))) && !l.match(new RegExp('(umpire|toss|fielding|batting|status|result)', 'i'))); let venueText = \"TBD\"; if (venueLine) { if (venueLine.includes('@')) { venueText = venueLine.split('@')[1].trim(); } else if (venueLine.includes(':')) { venueText = venueLine.substring(venueLine.indexOf(':') + 1).trim(); } else { venueText = venueLine; } venueText = venueText.split(new RegExp('(Umpires|Match|Status)', 'i'))[0].trim(); } let opponentText = teamsLine.replace(new RegExp('(📝|🏆|📄)', 'g'), '').trim(); if (opponentText.length > 5) { games.push({ date: dateText, time: timeText, opponent: opponentText, venue: venueText }); } } if (games.length === 0) { let tables = document.querySelectorAll('table'); let headers = {}; let targetTable = null; for (let table of tables) { let firstRow = table.querySelector('tr'); if (!firstRow) continue; let ths = Array.from(firstRow.querySelectorAll('th, td')).map(th => th.innerText.trim().toLowerCase()); if (ths.some(h => h.includes('date') || h.includes('match') || h.includes('opponent') || h.includes('versus') || h === 'vs' || h === 'v')) { targetTable = table; ths.forEach((text, i) => { if (text.includes('date')) { if (headers['date'] === undefined) headers['date'] = i; } else if ((text.includes('match') && !text.includes('type') && !text.includes('status')) || text.includes('teams') || text.includes('opponent') || text.includes('versus') || text === 'vs' || text === 'v') { if (headers['opponent'] === undefined) headers['opponent'] = i; } else if (text.includes('ground') || text.includes('venue') || text.includes('location')) { if (headers['venue'] === undefined) headers['venue'] = i; } else if (text.includes('time')) { if (headers['time'] === undefined) headers['time'] = i; } }); break; } } if (targetTable) { if (headers['date'] === undefined) headers['date'] = 0; if (headers['opponent'] === undefined) headers['opponent'] = 1; if (headers['venue'] === undefined) headers['venue'] = 2; let rows = targetTable.querySelectorAll('tr'); for (let i = 1; i < rows.length; i++) { let cells = rows[i].querySelectorAll('td'); if (cells.length < 3) continue; let dateText = cells[headers['date']]?.innerText.trim(); let opponentText = cells[headers['opponent']]?.innerText.trim(); let venueText = cells[headers['venue']]?.innerText.trim(); let timeText = headers['time'] !== undefined ? cells[headers['time']]?.innerText.trim() : null; if (dateText && !timeText && dateText.match(new RegExp('\\\\d{1,2}:\\\\d{2}'))) { let parts = dateText.split('\\n'); if (parts.length > 1) { dateText = parts[0].trim(); timeText = parts[1].trim(); } else { let timeMatch = dateText.match(new RegExp('(\\\\d{1,2}:\\\\d{2}\\\\s*(AM|PM|am|pm)?)')); if (timeMatch) { timeText = timeMatch[0]; dateText = dateText.replace(timeText, '').trim(); } } } if (dateText && opponentText) { games.push({ date: dateText, time: timeText || \"09:00 AM\", opponent: opponentText.replace(new RegExp('\\\\s+', 'g'), ' '), venue: venueText || \"TBD\" }); } } } } if (games.length === 0) { alert(\"Could not find any schedule cards. The table format might be different.\"); return; } let uniqueGames = []; let seen = new Set(); for (let g of games) { let key = g.date + g.opponent; if (!seen.has(key)) { seen.add(key); uniqueGames.push(g); } } let json = JSON.stringify(uniqueGames, null, 2); navigator.clipboard.writeText(json).then(function() { alert(\"Copied \" + uniqueGames.length + \" games to clipboard!\\nNow go to Clubhudl Admin Dashboard -> Sync Schedule to paste it.\"); }, function(err) { prompt(\"Failed to auto-copy. Please manually copy this JSON (Cmd/Ctrl+C):\", json); }); } parseSchedule();})();")}
                className="bg-gray-200 hover:bg-gray-300 px-3 border border-l-0 border-gray-300 rounded-r-md text-gray-600"
                title="Copy to clipboard"
              >
                <ClipboardPaste size={16} />
              </button>
            </div>
            <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
              <AlertCircle size={12} /> Drag this code to your bookmarks bar, or create a new bookmark and paste this as the URL.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">3. Paste Data Here</label>
        <textarea
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
          placeholder="[{ &quot;date&quot;: &quot;05/15/2026&quot;, &quot;opponent&quot;: &quot;...&quot; }]"
          rows={6}
          className="w-full font-mono text-sm border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500"
        />
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 text-red-700 flex items-start gap-3">
          <AlertTriangle className="flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {syncSuccess && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 text-green-700 flex items-start gap-3">
          <Check className="flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm font-medium">Successfully synced schedule to database!</p>
        </div>
      )}

      <div className="flex gap-4">
        <button
          onClick={handlePreview}
          disabled={!jsonInput.trim()}
          className="px-6 py-2 bg-slate-800 text-white font-semibold rounded-lg hover:bg-slate-900 disabled:opacity-50"
        >
          Preview Changes
        </button>
      </div>

      {previewData && (
        <div className="mt-8 border-t pt-8">
          <h3 className="text-xl font-bold mb-4">Preview Sync</h3>
          
          <div className="space-y-6">
            {previewData.newGames.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-bold text-green-800 mb-2">{previewData.newGames.length} New Games to Add</h4>
                <ul className="text-sm space-y-1">
                  {previewData.newGames.map((g, i) => (
                    <li key={i} className="text-green-700">
                      <span className="font-semibold">{g.date} {g.time}</span> vs {g.opponent} @ {g.venue}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {previewData.updatedGames.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-bold text-blue-800 mb-2">{previewData.updatedGames.length} Games with Changes</h4>
                <ul className="text-sm space-y-2">
                  {previewData.updatedGames.map((g, i) => (
                    <li key={i} className="text-blue-700 border-b border-blue-100 pb-2 last:border-0 last:pb-0">
                      <span className="font-semibold">{g.date}</span> vs {g.opponent}
                      <ul className="list-disc list-inside mt-1 ml-2 text-xs">
                        {g.changes.map((c, j) => <li key={j}>{c}</li>)}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {previewData.newGames.length === 0 && previewData.updatedGames.length === 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-600">
                <p>All {previewData.untouchedGames.length} games are already up to date. No changes needed.</p>
              </div>
            )}

            {(previewData.newGames.length > 0 || previewData.updatedGames.length > 0) && (
              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="w-full py-3 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {isSyncing ? 'Syncing to Database...' : 'Confirm & Sync'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncSchedule;
